/**
 * Occlusion culling for line-chart value labels: when several series' labels
 * collide at the same x-bucket, only the topmost survives — the reader gets
 * one label per visual cluster instead of a smear, and the per-series label
 * color carries attribution. Values within a cluster are within one
 * label-height of each other, so the surviving label is approximately true
 * for the whole cluster; exact values stay one hover away.
 *
 * Runs in real pixel space: labels render naively and a chart-level layout
 * pass measures the rendered <text> positions and hides the losers (no
 * plot-height guessing — the DOM is the source of truth).
 */

/**
 * Vertical clearance a label must have from the last kept label to survive.
 * One glyph-height apart (~fontSize) technically avoids overlap but reads as
 * cramped — labels brush each other and the neighbouring lines/dots, so
 * surviving labels need multiple glyph-heights of daylight (2.5× ≈ 33px at
 * the default 13px font; tuned visually against dense grouped-themes
 * charts).
 */
export function pointLabelClearancePx(fontSizePx: number): number {
  return Math.ceil(fontSizePx * 2.5)
}

/**
 * Indexes of labels to hide. Labels collide only within one x-bucket (they
 * share the same x, centered), so this is a per-bucket 1-D pass: sort by y,
 * keep the topmost, then keep each next label only when it clears the last
 * kept one by the given clearance.
 */
export function selectCulledLabelIndexes(
  labels: ReadonlyArray<{ x: number; y: number }>,
  labelHeightPx: number,
): Set<number> {
  const byBucket = new Map<number, Array<{ y: number; index: number }>>()
  labels.forEach(({ x, y }, index) => {
    const key = Math.round(x)
    const bucket = byBucket.get(key)
    if (bucket) {
      bucket.push({ y, index })
    } else {
      byBucket.set(key, [{ y, index }])
    }
  })

  const culled = new Set<number>()
  byBucket.forEach((bucket) => {
    bucket.sort((a, b) => a.y - b.y)
    let lastKeptY = -Infinity
    bucket.forEach(({ y, index }) => {
      if (y - lastKeptY < labelHeightPx) {
        culled.add(index)
      } else {
        lastKeptY = y
      }
    })
  })
  return culled
}
